import { VOTER_SUFFIX } from "../../../constants";
import { MESSAGES } from "../../../messages";
import {
  Proposal,
  VoteAction,
  VotingAlignment,
  VotingRecordItem,
} from "../../../models/stewardshipModels";
import { VotingDataSourceType } from "../../../models/tableModels";
import { Content } from "../ContentBox";

export const buildVotingRecords = (data: VotingRecordItem[]): Proposal[] => {
  const proposals: Proposal[] = data.map((item) => ({
    voting_record_id: item.voting_record_id,
    proposal_id: item.proposal_id.toString(),
    proposal_description: item.proposal_text,
    proposal_type: item.proposal_subcategory,
    management_recommendation: item.management_recommendation,
    voter_id: item.manager_id,
    voter_name: item.manager_name,
    vote_type: item.vote,
    flag_status: item.flag_status,
    rationale: item.rationale,
  }));

  return proposals;
};

export const calculateVotingAlignment = (
  votingData: Proposal[],
  referenceManager: string | null = null,
): VotingAlignment[] => {
  // Step 0: Derive the list of managers
  const managers = votingData.reduce<{ id: string; name: string }[]>(
    (uniqueManagers, vote) => {
      if (!uniqueManagers.some((manager) => manager.id === vote.voter_id)) {
        uniqueManagers.push({ id: vote.voter_id, name: vote.voter_name });
      }
      return uniqueManagers;
    },
    [],
  );

  // Step 0: If referenceManager is provided, get their votes
  const referenceVotes = referenceManager
    ? votingData.filter((vote) => vote.voter_id === referenceManager)
    : null;

  return managers.map((manager) => {
    // Get all of the current manager's votes
    const managerVotes = votingData.filter(
      (vote) => vote.voter_id === manager.id,
    );

    let alignedVotesCount = 0;
    let consideredVotesCount = 0; // denominator

    // ---- Step 1: Only consider proposals that exist for both managers ----
    // If we have a reference manager, let's filter the managerVotes to proposals
    // that the reference manager also voted on (the intersection).
    let managerVotesIntersection = managerVotes;
    if (referenceVotes) {
      managerVotesIntersection = managerVotes.filter((mv) =>
        referenceVotes.some((rv) => rv.proposal_id === mv.proposal_id),
      );
    }

    // ---- Step 2: Compute alignment ----
    // Note: If referenceManager is null, fall back to management recommendation
    if (referenceVotes) {
      // Compare managerVotesIntersection directly to the reference manager’s votes
      alignedVotesCount = managerVotesIntersection.filter((vote) => {
        // For each of the manager's votes, get the reference manager's matching vote
        const refVote = referenceVotes.find(
          (rv) => rv.proposal_id === vote.proposal_id,
        );
        // If there's a matching vote, check if the vote_type is the same
        return refVote && refVote.vote_type === vote.vote_type;
      }).length;
      consideredVotesCount = managerVotesIntersection.length;
    } else {
      // Default to alignment with management_recommendation
      alignedVotesCount = managerVotesIntersection.filter((vote) => {
        return vote.vote_type === vote.management_recommendation;
      }).length;
      consideredVotesCount = managerVotesIntersection.length;
    }

    // Avoid division by zero
    const alignedPct = consideredVotesCount
      ? alignedVotesCount / consideredVotesCount
      : 0;

    return {
      managerId: manager.id,
      manager: manager.name,
      alignedPct,
    };
  });
};

export const getContentBoxRows = (
  votingRecord: VotingRecordItem | null,
): Content[][] => {
  return [
    [
      {
        title: "Proposal Category",
        content: votingRecord?.proposal_category || MESSAGES.NOT_AVAILABLE,
      },
      {
        title: "Proposal Subcategory",
        content: votingRecord?.proposal_subcategory || MESSAGES.NOT_AVAILABLE,
      },
    ],
    [
      {
        title: "Proponent",
        content: votingRecord?.proponent || MESSAGES.NOT_AVAILABLE,
      },
      {
        title: "Management Recommendation",
        content:
          votingRecord?.management_recommendation || MESSAGES.NOT_AVAILABLE,
      },
    ],
    [
      {
        title: `${votingRecord?.manager_name || MESSAGES.NOT_AVAILABLE} Vote`,
        content: votingRecord?.vote || MESSAGES.NOT_AVAILABLE,
      },
    ],
    [
      {
        title: `${
          votingRecord?.manager_name || MESSAGES.NOT_AVAILABLE
        } Rationale`,
        content: votingRecord?.rationale || MESSAGES.NOT_AVAILABLE,
      },
    ],
  ];
};

export const retrieveVotingRecordId = (
  record: VotingDataSourceType,
  voterColumnKey: string,
): string => {
  const managerId = voterColumnKey.replace(VOTER_SUFFIX, "");
  const managerVoteInfo = record[managerId] as VoteAction;
  return managerVoteInfo?.votingRecordId || MESSAGES.NOT_AVAILABLE;
};
